import { Box, Divider, Group, Input, Select, Tooltip } from '@byecode/ui'
import type { FlowNode, NodeTypes, SendMsgWxRootConfig, SendMsgWxRootConfigMsgType } from '@lighthouse/shared'
import {
    getAllCanBeUpstreamNodes,
    getInputInnerTypeMatch,
    getUpstreamNodesOptions,
    NodeFieldPreview,
    useAtomData
} from '@lighthouse/shared'
import React, { useMemo } from 'react'
import { useSetState } from 'react-use'

import { otherPlatformListAtom } from '@/atoms/application/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetUserRecordOptions } from '@/hooks/usePage'

import { GroupRequired } from '../../Common/GroupRequired'
import { UnitNoAuth } from '../../Common/UnitNoAuth'
import { SEND_MSG_WX_OPTIONS } from '../../constant'
import * as CM from '../../styles'
import * as SC from './styles'

interface WxRobotActionConfigureProps {
    nodeType?: NodeTypes
    allParentNodes: FlowNode[]
    value: SendMsgWxRootConfig
    onChange: (value: SendMsgWxRootConfig) => void
}

export const WxRobotActionConfigure: React.FC<WxRobotActionConfigureProps> = ({ allParentNodes, value, onChange }) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const disabledWithVersion = useIsDisabledWithVersion()
    const [{ webhookError }, setState] = useSetState<{ webhookError?: string }>({})
    const otherPlatformList = useAtomData(otherPlatformListAtom)

    const options = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                dataSourceList,
                upstreamNodes: getAllCanBeUpstreamNodes(allParentNodes),
                allUpstreamNodes: allParentNodes
            }),
        [allParentNodes, dataSourceList, workflowList]
    )

    const userOption = useGetUserRecordOptions()

    const weComRobotAccount = useMemo(() => otherPlatformList?.find(item => item.type === 'WE_COM_ROBOT'), [otherPlatformList])

    if (!weComRobotAccount) {
        return <UnitNoAuth type="WE_COM_ROBOT" />
    }

    return (
        <>
            <CM.Container>
                {/* <ActionAuthIntegrateCard type="WE_COM_ROBOT" />
                <Divider color="var(--color-gray-200)" /> */}
                <GroupRequired label="企业微信机器人配置">
                    <Box px={8}>
                        <SC.RowList>
                            <SC.RowItem>
                                <SC.RowItemLeft>
                                    <SC.RowText>Webhook地址</SC.RowText>
                                    <SC.Text color="red" style={{ paddingLeft: 4, marginRight: 4 }}>
                                        *
                                    </SC.Text>
                                    <Tooltip
                                        title={
                                            <SC.TooltipContent>
                                                选择一个企业微信内部员工群，右上角“群设
                                                <br />
                                                置”&gt;群管理&gt;机器人&gt;添加机器人&gt;添加“自定义机
                                                <br />
                                                器人”，添加成功后，复制 Webhook 地址
                                            </SC.TooltipContent>
                                        }
                                    >
                                        <SC.Icon type="Question" size={16} color="var(--color-gray-400)" />
                                    </Tooltip>
                                </SC.RowItemLeft>
                                <SC.RowItemRight>
                                    <Input
                                        placeholder="请粘贴"
                                        style={{ width: 180 }}
                                        value={value.webhook}
                                        onChange={e => {
                                            const webhook = e.target.value
                                            onChange({ ...value, webhook })
                                        }}
                                    />
                                </SC.RowItemRight>
                            </SC.RowItem>
                            <SC.RowItem>
                                <SC.RowItemLeft>
                                    <SC.RowText>执行操作</SC.RowText>
                                </SC.RowItemLeft>
                                <SC.RowItemRight>
                                    <SC.SelectContainer>
                                        <Select
                                            placeholder="请选择"
                                            styles={{
                                                root: {
                                                    width: 180
                                                }
                                            }}
                                            options={SEND_MSG_WX_OPTIONS}
                                            value={value.msgtype}
                                            onChange={msgtype => onChange({ ...value, msgtype: msgtype as SendMsgWxRootConfigMsgType })}
                                        />
                                    </SC.SelectContainer>
                                </SC.RowItemRight>
                            </SC.RowItem>
                        </SC.RowList>
                    </Box>
                </GroupRequired>
                <Divider color="var(--color-gray-200)" />
                <Group label="配置参数">
                    <Box px={8} mt={8}>
                        <SC.Item>
                            <SC.Text>
                                消息内容
                                <SC.Text color="red" style={{ paddingLeft: 4 }}>
                                    *
                                </SC.Text>
                            </SC.Text>
                            <SC.FormItemWrapper>
                                {/* <Input placeholder="请输入" {...register('config.text.content')} /> */}
                                {/* <NodeFieldPreview disabled={disabledWithVersion} autoHeight upstreamOptions={options} /> */}
                                <NodeFieldPreview
                                    innerTypes={getInputInnerTypeMatch('TEXT')}
                                    disabled={disabledWithVersion}
                                    value={value.text.editorContent}
                                    onBlur={editorContent => onChange({ ...value, text: { ...value.text, editorContent } })}
                                    autoHeight
                                    userOption={userOption}
                                    upstreamOptions={options}
                                    dataSourceList={dataSourceList}
                                />
                            </SC.FormItemWrapper>
                            {/* <SC.Description>方案启动后，填入的文本内容，将被企微群机器人发出，请检查核对。</SC.Description> */}
                        </SC.Item>
                        <SC.Item>
                            <SC.Text>
                                提醒人员
                                <Tooltip
                                    title={
                                        <SC.TooltipContent>
                                            可以输入@all，提醒全部群成员，也可以输入成
                                            <br />
                                            员ID提醒多人，并使用中/英文逗号隔开，比如
                                            <br />
                                            @all，Wendywang、Kevinliu、Tonychen。
                                            <br />
                                            成员ID可以在企业微信管理后台&gt;通讯录&gt;成员详
                                            <br />
                                            情中查看，“账号”即是成员ID，一般是一串英文
                                        </SC.TooltipContent>
                                    }
                                >
                                    <SC.Icon type="Question" size={16} color="var(--color-gray-400)" ml={7} />
                                </Tooltip>
                            </SC.Text>
                            <SC.FormItemWrapper>
                                {/* <NodeFieldPreview disabled={disabledWithVersion} autoHeight upstreamOptions={options} /> */}
                                <NodeFieldPreview
                                    innerTypes={getInputInnerTypeMatch('TEXT')}
                                    disabled={disabledWithVersion}
                                    value={value.text.mentionedContent}
                                    onBlur={mentionedContent => onChange({ ...value, text: { ...value.text, mentionedContent } })}
                                    autoHeight
                                    userOption={userOption}
                                    upstreamOptions={options}
                                    dataSourceList={dataSourceList}
                                />
                            </SC.FormItemWrapper>
                            {/* <SC.Description>
                            可以输入@all，提醒全部群成员，也可以输入成员ID提醒多人，并使用中/英文逗号隔开，比如@all，Wendywang、Kevinliu、Tonychen。
                            <br /> <br />
                            成员ID可以在企业微信管理后台-通讯录-成员详情中查看，“账号”即是成员ID，一般是一串英文
                            </SC.Description> */}
                        </SC.Item>
                    </Box>
                </Group>
                {/* 添加企业微信机器人 */}
                {/* <WxRobotModal open={opened} id={id} mode={mode} onClose={() => setState({ opened: false })} /> */}
            </CM.Container>
        </>
    )
}
